import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Alert,
  CircularProgress,
  Snackbar,
  TablePagination,
  Chip,
  Divider,
  Stack,
  Tooltip,
  IconButton,
  useTheme,
  alpha,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import axios from "axios";
import { decryptText, decryptAppId } from "../../../utils/encryption";

const ParameterAllotment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const getParamFromQuery = (paramName, isAppId = false) => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedParam = searchParams.get(paramName);
    if (encryptedParam) {
      try {
        return isAppId ? decryptAppId(encryptedParam) : decryptText(encryptedParam);
      } catch {
        return encryptedParam;
      }
    }
    return null;
  };

  const [appId] = useState(getParamFromQuery("appId", true));
  const [assessmentId] = useState(getParamFromQuery("assessmentId"));
  const [assessorId] = useState(getParamFromQuery("assessorId"));
  const [labTypeId] = useState(getParamFromQuery("labTypeId"));

  const [disciplines, setDisciplines] = useState([]);
  const [groups, setGroups] = useState([]);
  const [scopeData, setScopeData] = useState([]);

  const [selectedDiscipline, setSelectedDiscipline] = useState("0");
  const [selectedGroup, setSelectedGroup] = useState("0");
  const [selectedAction, setSelectedAction] = useState("0");

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectAll, setSelectAll] = useState(false);

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    fetchDisciplines();
  }, []);

  useEffect(() => {
    if (selectedDiscipline !== "0") {
      fetchGroups();
    } else {
      setGroups([]);
      setSelectedGroup("0");
    }
  }, [selectedDiscipline]);

  useEffect(() => {
    fetchScopeData();
  }, [selectedDiscipline, selectedGroup, selectedAction, currentPage, rowsPerPage]);

  const fetchDisciplines = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/assessor/parameter-allotment/disciplines`,
        {
          params: {
            appId,
            orgId: localStorage.getItem("orgId") || "0",
            assessmentId,
            assessorId,
            labTypeId,
          },
        }
      );
      if (response.data?.data) {
        setDisciplines(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching disciplines:", error);
      showSnackbar("Failed to load disciplines", "error");
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/assessor/parameter-allotment/groups`,
        {
          params: {
            appId,
            orgId: localStorage.getItem("orgId") || "0",
            assessmentId,
            assessorId,
            labTypeId,
            disciplineId: selectedDiscipline,
          },
        }
      );
      if (response.data?.data) {
        setGroups(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
      showSnackbar("Failed to load groups", "error");
    }
  };

  const fetchScopeData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/assessor/parameter-allotment/scope`,
        {
          params: {
            appId,
            orgId: localStorage.getItem("orgId") || "0",
            disciplineId: selectedDiscipline,
            groupId: selectedGroup,
            action: selectedAction,
            page: currentPage,
            pageSize: rowsPerPage,
            assessmentId,
            assessorId,
            labTypeId,
          },
        }
      );

      if (response.data?.data && Array.isArray(response.data.data)) {
        const dataWithSelection = response.data.data.map((item) => ({
          ...item,
          selected: false,
        }));
        setScopeData(dataWithSelection);
        setTotalCount(response.data.totalCount || 0);
      } else {
        setScopeData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching scope data:", error);
      showSnackbar("Error fetching scope data", "error");
      setScopeData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    const checked = event.target.checked;
    setSelectAll(checked);
    setScopeData((prev) => prev.map((item) => ({ ...item, selected: checked })));
  };

  const handleRowSelect = (index) => {
    setScopeData((prev) => {
      const updated = [...prev];
      updated[index].selected = !updated[index].selected;
      return updated;
    });
  };

  useEffect(() => {
    if (scopeData.length > 0) {
      setSelectAll(scopeData.every((item) => item.selected));
    } else {
      setSelectAll(false);
    }
  }, [scopeData]);

  const handleAllotParameters = async () => {
    const selectedItems = scopeData.filter((item) => item.selected);
    if (selectedItems.length === 0) {
      showSnackbar("Please select at least one parameter", "warning");
      return;
    }

    setLoading(true);
    try {
      const promises = selectedItems.map((item) =>
        axios.post(
          `${process.env.REACT_APP_API_URL}/assessor/parameter-allotment/allot`,
          {
            appId,
            scopeId: item.Id,
            labTypeId,
            orgId: localStorage.getItem("orgId") || "0",
            assessmentId,
            assessorId,
            mode: selectedAction === "0" ? 1 : 2,
          }
        )
      );

      await Promise.all(promises);

      showSnackbar(
        selectedAction === "0"
          ? "Parameters allotted successfully"
          : "Parameters removed successfully",
        "success"
      );

      setSelectAll(false);
      fetchScopeData();
    } catch (error) {
      console.error("Error allotting parameters:", error);
      showSnackbar("Error processing parameters", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const renderScopeTable = () => {
    const isTestingOrCalibration = labTypeId === "1" || labTypeId === "2";
    const isMedical = labTypeId === "3";

    const headers = isTestingOrCalibration
      ? [
          "S.No",
          "Assessor",
          "Test Code",
          "Discipline",
          "Group",
          "Equipment/Device",
          "Parameter",
          "Standard & Method",
          "Range",
          "CMC(±)",
          "Performed at",
          "Capability Mode",
          "Remarks",
        ]
      : isMedical
      ? [
          "S.No",
          "Assessor",
          "Test Code",
          "Discipline",
          "Equipment/Device",
          "Parameter",
          "Standard & Method",
          "Range",
          "Performed at",
          "Remarks",
        ]
      : [];

    return (
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          boxShadow: theme.shadows[2],
          overflowX: "auto",
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: theme.palette.primary.main,
                "& th": {
                  color: theme.palette.common.white,
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  whiteSpace: "nowrap",
                  py: 1.5,
                },
              }}
            >
              <TableCell padding="checkbox" sx={{ backgroundColor: "inherit" }}>
                <Checkbox
                  checked={selectAll}
                  onChange={handleSelectAll}
                  disabled={scopeData.length === 0 || loading}
                  sx={{
                    color: theme.palette.common.white,
                    "&.Mui-checked": {
                      color: theme.palette.common.white,
                    },
                  }}
                />
              </TableCell>
              {headers.map((label) => (
                <TableCell key={label} sx={{ backgroundColor: "inherit" }}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {scopeData.map((row, index) => (
              <TableRow
                key={row.Id || index}
                hover
                sx={{
                  "&:nth-of-type(odd)": {
                    backgroundColor: alpha(theme.palette.primary.light, 0.05),
                  },
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.light, 0.12),
                  },
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={row.selected || false}
                    onChange={() => handleRowSelect(index)}
                    disabled={loading}
                  />
                </TableCell>
                <TableCell>{row.r || index + 1}</TableCell>
                <TableCell>
                  {row.AssessorName ? (
                    row.AssessorName
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Not Allotted
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{row.TestCode || "-"}</TableCell>
                <TableCell>{row.DisciplineName || "-"}</TableCell>
                {isTestingOrCalibration && (
                  <TableCell>{row.GroupName || "-"}</TableCell>
                )}
                <TableCell>{row.GroupTested || "-"}</TableCell>
                <TableCell>{row.TypeOfTest || "-"}</TableCell>
                <TableCell>{row.ScopeStandard || "-"}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    {row.rangeType || "-"}
                  </Typography>
                  <Typography variant="body2" fontSize="0.75rem">
                    {row.RangeLL || row.RangeUL
                      ? `${row.RangeLL || "-"} ${row.RangeLLUnit || ""} To ${row.RangeUL || "-"} ${row.RangeULUnit || ""}`
                      : "-"}
                  </Typography>
                </TableCell>
                {isTestingOrCalibration && (
                  <TableCell>
                    {row.cv || row.cmcuppervalue
                      ? `${row.cv || "-"} ${row.mu || ""} To ${row.cmcuppervalue || "-"} ${row.cmcupperUnit || ""}`
                      : "-"}
                  </TableCell>
                )}
                <TableCell>{row.testPerformedAt || "-"}</TableCell>
                {isTestingOrCalibration && (
                  <TableCell>{row.CapabilityMode || "-"}</TableCell>
                )}
                <TableCell>{row.rangeRemark || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const selectedCount = scopeData.filter((item) => item.selected).length;

  return (
    <Box sx={{ p: { xs: 1, md: 1 }, backgroundColor: "#f8f9fe", minHeight: "100vh" }}>
      <Card
        elevation={4}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        {/* Header with gradient */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            px: 1,
            py: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: "white",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <AssignmentIcon fontSize="large" />
            Parameter Allotment – Scope of Accreditation
          </Typography>
        </Box>

        <CardContent sx={{ p: 3 }}>
          {/* Filters Card */}
          <Paper
            elevation={1}
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.primary.light, 0.04),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>
                    <FilterListIcon sx={{ mr: 1, fontSize: "1rem", verticalAlign: "middle" }} />
                    Discipline
                  </InputLabel>
                  <Select
                    value={selectedDiscipline}
                    onChange={(e) => {
                      setSelectedDiscipline(e.target.value);
                      setCurrentPage(1);
                    }}
                    label="Discipline"
                    sx={{ bgcolor: "white" }}
                  >
                    <MenuItem value="0">--Select--</MenuItem>
                    {disciplines.map((disc) => (
                      <MenuItem key={disc.id} value={disc.id}>
                        {disc.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {(labTypeId === "1" || labTypeId === "2") && (
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Group</InputLabel>
                    <Select
                      value={selectedGroup}
                      onChange={(e) => {
                        setSelectedGroup(e.target.value);
                        setCurrentPage(1);
                      }}
                      label="Group"
                      disabled={selectedDiscipline === "0"}
                      sx={{ bgcolor: "white" }}
                    >
                      <MenuItem value="0">--Select--</MenuItem>
                      {groups.map((grp) => (
                        <MenuItem key={grp.id} value={grp.id}>
                          {grp.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={selectedAction}
                    onChange={(e) => {
                      setSelectedAction(e.target.value);
                      setCurrentPage(1);
                    }}
                    label="Status"
                    sx={{ bgcolor: "white" }}
                  >
                    <MenuItem value="0">Pending</MenuItem>
                    <MenuItem value="1">Allotted</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Action Bar */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
              mb: 2.5,
            }}
          >
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Tooltip title="Allot or remove selected parameters">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleAllotParameters}
                  disabled={loading || selectedCount === 0}
                  sx={{
                    px: 3,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    boxShadow: theme.shadows[2],
                  }}
                >
                  {selectedAction === "0" ? "Allot Selected" : "Remove Allotment"}
                  {selectedCount > 0 && (
                    <Chip
                      label={selectedCount}
                      size="small"
                      sx={{
                        ml: 1,
                        backgroundColor: alpha(theme.palette.common.white, 0.2),
                        color: "white",
                      }}
                    />
                  )}
                </Button>
              </Tooltip>
              {selectedCount > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {selectedCount} selected
                </Typography>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => fetchScopeData()}
                sx={{ borderRadius: 2, textTransform: "none" }}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ClearIcon />}
                onClick={() => {
                  setSelectedDiscipline("0");
                  setSelectedGroup("0");
                  setSelectedAction("0");
                  setCurrentPage(1);
                }}
                sx={{ borderRadius: 2, textTransform: "none" }}
              >
                Clear Filters
              </Button>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Table and Pagination */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={48} />
            </Box>
          ) : scopeData.length > 0 ? (
            <>
              {renderScopeTable()}
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={currentPage - 1}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderBottom: "none",
                  mt: 1,
                  ".MuiTablePagination-toolbar": {
                    px: 2,
                  },
                }}
              />
            </>
          ) : (
            <Alert
              severity="info"
              icon={<FilterListIcon />}
              sx={{
                borderRadius: 2,
                py: 2,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography variant="body1" fontWeight={500}>
                No parameters found. Please adjust your filters.
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: 2, boxShadow: theme.shadows[4] }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ParameterAllotment;